import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
    const [form, setForm] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const { refresh } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/login", form);
            await refresh();
            navigate("/dashboard");
        } catch (err) {
            toast.error("Sign in failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
            <div className="hidden lg:flex flex-col justify-between p-10 bg-[#0F172A] text-white relative overflow-hidden">
                <Logo />
                <div className="max-w-md relative z-10">
                    <p className="overline text-slate-400 mb-4">Trusted by boutique agencies</p>
                    <blockquote className="font-display text-3xl leading-snug tracking-tight">
                        "Our clients think we hired a design ops team. It's just ClientSpace."
                    </blockquote>
                    <p className="mt-6 text-sm text-slate-400">— Marta Bianchi, Founder at Northwind</p>
                </div>
                <div className="text-xs text-slate-500 relative z-10">© 2026 ClientSpace</div>
                <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-[#C55A46]/15 blur-3xl pointer-events-none" />
            </div>

            <div className="flex items-center justify-center p-6 lg:p-10">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8"><Logo /></div>
                    <h1 className="font-display text-3xl tracking-tight text-slate-900">Welcome back</h1>
                    <p className="text-slate-500 mt-2">Sign in to your ClientSpace workspace.</p>

                    <button
                        onClick={startGoogleAuth}
                        data-testid="google-signin-btn"
                        className="mt-8 w-full flex items-center justify-center gap-3 py-3 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium hover:border-slate-300 transition-colors"
                    >
                        <GoogleIcon /> Continue with Google
                    </button>

                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-xs text-slate-400">or with email</span>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700">Email</Label>
                            <Input id="email" type="email" required autoComplete="email"
                                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="login-email" className="ring-terra" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-slate-700">Password</Label>
                            <Input id="password" type="password" required autoComplete="current-password"
                                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="login-password" className="ring-terra" />
                        </div>
                        <button type="submit" disabled={loading} data-testid="login-submit"
                            className="btn-terra w-full py-3 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
                        </button>
                    </form>

                    <p className="text-sm text-slate-500 mt-6 text-center">
                        New to ClientSpace? <Link to="/signup" className="text-[#C55A46] font-medium hover:underline" data-testid="link-signup">Create workspace</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.6 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.6 19 12.5 24 12.5c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.6 29 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.6-1.9 13.1-5l-6.1-5c-2 1.4-4.5 2.3-7 2.3-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.1 5c-.4.4 6.5-4.7 6.5-14.7 0-1.3-.1-2.3-.4-3.5z"/></svg>
    );
}
